import Axios, { AxiosRequestConfig } from "axios";

import {
  API_GATEWAY_ENDPOINT,
  API_GATEWAY_PATIENT_ENDPOINT,
} from "../util/env";

export const AXIOS_INSTANCE = Axios.create({
  // eslint-disable-next-line no-undef
  baseURL: API_GATEWAY_ENDPOINT,
});

export const PATIENT_AXIOS_INSTANCE = Axios.create({
  // eslint-disable-next-line no-undef
  baseURL: API_GATEWAY_PATIENT_ENDPOINT,
});

// cdk_resources の認証層が返す「患者アカウント無効化」マーカー
// レスポンスボディの code フィールドで識別する
const PATIENT_DISABLED_CODE = "PATIENT_DISABLED";

// 無効化検知時: localStorage をクリアしてログイン画面に遷移する
// 通常の 401（トークン期限切れ）は Session.tsx の refresh 試行フローに任せ、
// ここでは PATIENT_DISABLED マーカー付き 401 のみをハンドリングする
const handlePatientDisabledResponse = (error: unknown) => {
  if (!Axios.isAxiosError(error)) return;
  const { response } = error;
  if (response?.status !== 401) return;
  const code = (response.data as { code?: string } | undefined)?.code;
  if (code !== PATIENT_DISABLED_CODE) return;

  localStorage.clear();
  // eslint-disable-next-line no-undef
  window.location.href = "/login?reason=account_disabled";
};

AXIOS_INSTANCE.interceptors.response.use(
  (res) => res,
  (err) => {
    handlePatientDisabledResponse(err);
    return Promise.reject(err);
  }
);

PATIENT_AXIOS_INSTANCE.interceptors.response.use(
  (res) => res,
  (err) => {
    handlePatientDisabledResponse(err);
    return Promise.reject(err);
  }
);

const ID_TOKEN_KEY = "idToken";

export const useCustomClient = <T>(): ((
  config: AxiosRequestConfig
) => Promise<T>) => {
  const token = localStorage.getItem(ID_TOKEN_KEY);

  return (config: AxiosRequestConfig & { tag?: string }) => {
    const source = Axios.CancelToken.source();

    // patient用のAPI Gatewayの場合はエンドポイントを切り替える
    const isPatientApiGateway = config?.url?.match(
      `^/[^/]+/apiGatewayPatient(/|$)`
    );

    if (isPatientApiGateway) {
      const promise = PATIENT_AXIOS_INSTANCE({
        ...config,
        cancelToken: source.token,
        headers: { Authorization: `Bearer ${token}` },
      }).then(({ data }) => data);

      // @ts-ignore
      promise.cancel = () => {
        source.cancel("Query was cancelled by React Query");
      };
      return promise;
    } else {
      const promise = AXIOS_INSTANCE({
        ...config,
        cancelToken: source.token,
        headers: { Authorization: `Bearer ${token}` },
      }).then(({ data }) => data);

      // @ts-ignore
      promise.cancel = () => {
        source.cancel("Query was cancelled by React Query");
      };
      return promise;
    }
  };
};

export default useCustomClient;

export type ErrorType<ErrorData> = ErrorData;

export type BodyType<BodyData> = BodyData & { headers?: any };
